import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, unref as _unref, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "modal-mask"
}
const _hoisted_2 = { class: "modal-wrapper" }
const _hoisted_3 = {
  class: "modal-container-calendar",
  ref: "target"
}
const _hoisted_4 = { class: "modal-body" }

import { watch, computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { ApiError, DateFormat, ItemSelect, ItemTag } from '@/models/share.model';
import { AppointmentTypeTimeslotsRequest, SpecificDaySinceDayModelRequest, DayModelContentResponse, DayModelResponse, PersonLightResponse, SettingsApi, TimeslotRequest } from '@/apiGenerate/medserver';
import { medServerApiConfig } from '@/config/medserver-api.config';
import { useAppUserStore } from '@/stores/appUser';
import { toastError } from '@/utils/toast-utils';
import { useToast } from 'primevue';
import { DurationInput, EventClickArg, EventInput } from '@fullcalendar/core';
import { DateClickArg } from '@fullcalendar/interaction';
import EsCalendarDayModelContent from '@/components/calendar/esCalendarDayModelContent.vue';
import { format } from 'date-fns';
import { formatTime} from '@/utils/date-utils';
import EsTimeslotForm from '@/components/form/esTimeslotForm.vue';
import { ApointmentTypeTimeslotsWithIndex } from '@/models/calendar.model';
import { cloneDeep, isEqual } from 'lodash';


export default /*@__PURE__*/_defineComponent({
  __name: 'esCalendarSettings',
  props: {
    isOpen: { type: Boolean },
    header: {},
    selectedDate: {},
    selectedDoctor: {}
  },
  emits: ["modal-close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emits = __emit;

const { t } = useI18n();
const toast = useToast();
const appUserStore = useAppUserStore();

const settingsApi = new SettingsApi(medServerApiConfig);
const dayModel = ref<DayModelResponse>(undefined);
const dayModelItemSelect = ref<ItemSelect>(undefined);
const selectedAppointmentType = ref<ItemSelect>(null);
const isEsTimeslotFormOpened = ref<boolean>(false);
const selectedHealthPro = ref<PersonLightResponse>({
  id: props.selectedDoctor.id ?? undefined,
});
const selectedTimeSlot = ref<TimeslotRequest>({
  start: '',
  end: '',
  timeslotId: undefined
});
const today = new Date();
const minTime = new Date(today);
minTime.setHours(0, 0, 0, 0);
const maxTime = new Date(today);
maxTime.setHours(24, 0, 0, 0);
const refreshData = ref<boolean>(false);
const isSameDate = ref<boolean>(true);
const displayDeleteButton = ref<boolean>(false);
const durationInput = ref<DurationInput>(null);

const specificDaySinceDayModelRequest = ref<SpecificDaySinceDayModelRequest>({
  dayModelName: props.selectedDate,
  periodStart: props.selectedDate,
  periodEnd: props.selectedDate,
  appointmentTypeTimeslots: []
});

const apointmentTypeTimeslotsWithIndex = ref<ApointmentTypeTimeslotsWithIndex[]>([]);
const backupApointmentTypeTimeslotsWithIndex = ref<ApointmentTypeTimeslotsWithIndex[]>([]);

const currInstituteUuid = computed(() => {
  if (!appUserStore.currentHealthGroupMembership) {return "";}
  const currentIwr = appUserStore.currentHealthGroupMembership.instituteWithRole;
  return currentIwr.instituteWithRoleId;
});

const noChange = computed(() => {
  return isEqual( backupApointmentTypeTimeslotsWithIndex.value, apointmentTypeTimeslotsWithIndex.value);
});


const transformToAppointmentTypeTimeslotsRequest= (apointmentTypeTimeslotsWithIndex: ApointmentTypeTimeslotsWithIndex) : AppointmentTypeTimeslotsRequest => {
  return {
    appointmentTypeId: apointmentTypeTimeslotsWithIndex.apointmentTypeTimeslots.appointmentTypeId,
    start: format(new Date(apointmentTypeTimeslotsWithIndex.apointmentTypeTimeslots.start), DateFormat.TIME_LONG),
    end: format(new Date(apointmentTypeTimeslotsWithIndex.apointmentTypeTimeslots.end), DateFormat.TIME_LONG),
  }
}

const confirmModal = () => {
  specificDaySinceDayModelRequest.value.appointmentTypeTimeslots = apointmentTypeTimeslotsWithIndex.value.map((apointmentTypeTimeslotsWithIndex: ApointmentTypeTimeslotsWithIndex) => transformToAppointmentTypeTimeslotsRequest(apointmentTypeTimeslotsWithIndex));

  emits('modal-close', {specificDaySinceDayModelRequest: specificDaySinceDayModelRequest.value, dayModelId: dayModel.value.quickParam ? dayModel.value.dayModelId : null});
};

const transformToItemSelect = (dataToTransform: DayModelResponse): ItemSelect => {
    if (dataToTransform) {
      return {
        id: dataToTransform.dayModelId,
        label: dataToTransform.name
      }
    }
}

const getDayModel = async () => {
  if (props.selectedDoctor?.id && props.selectedDate && currInstituteUuid.value) {
    await settingsApi.getDayModelByDate({instituteId: currInstituteUuid.value, doctorId: props.selectedDoctor.id, date: props.selectedDate}).then((result: DayModelResponse) => {
      dayModel.value = result;
      dayModelItemSelect.value = transformToItemSelect(result);
    }).catch((error: ApiError) => {
      toastError(toast, {
          summary: t('common.toast.error'),
          detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
      })
    });
  }
};

const getLastIndexForAppointmentType = (appointmentTypeId: string) => {
  const listFiltered = apointmentTypeTimeslotsWithIndex.value
        .filter((appointmentTypeTimeslot: ApointmentTypeTimeslotsWithIndex) => appointmentTypeTimeslot.apointmentTypeTimeslots.appointmentTypeId === appointmentTypeId);

  if (!listFiltered || listFiltered.length == 0) {
    return appointmentTypeId + '_' + 0;
  }

  const newIndex = Number(listFiltered
        .sort((a: ApointmentTypeTimeslotsWithIndex, b: ApointmentTypeTimeslotsWithIndex) => {return - a.index.localeCompare(b.index)})[0]
        .index
        .split('_')[1]) + 1

    return appointmentTypeId + '_' + newIndex;
}

const updateEvent = async(data: {id: string, start: string, end: string, selectedAppointmentType: ItemTag}) => {
  selectedAppointmentType.value = data.selectedAppointmentType;
  const timeslotRequest: TimeslotRequest = {
    timeslotId: data.id,
    start: format(new Date(data.start), DateFormat.TIME_LONG),
    end: format(new Date(data.end), DateFormat.TIME_LONG),
  };

  updateTimeslot(timeslotRequest)
}


const openAddTimeslotForm = async (data : {info: DateClickArg, item: ItemTag}) => {
  selectedAppointmentType.value = data.item ?? null;

  if (data.info && data.info.date) {
    selectedTimeSlot.value.start = format(data.info.date, DateFormat.TIME_LONG);
    selectedTimeSlot.value.end = format(data.info.date, DateFormat.TIME_LONG);

    selectedTimeSlot.value.timeslotId = getLastIndexForAppointmentType(selectedAppointmentType.value.id);

    displayDeleteButton.value = false;

    isEsTimeslotFormOpened.value = true;
  }
}

const openUpdateTimeslotForm = (data : {info: EventClickArg, item: ItemTag}) => {
  selectedAppointmentType.value = data.item ?? null;

  if (data.info?.event) {    
    selectedTimeSlot.value.start = format(data.info.event.start, DateFormat.TIME_LONG);
    selectedTimeSlot.value.end = format(data.info.event.end, DateFormat.TIME_LONG);
    selectedTimeSlot.value.timeslotId = data.info.event.id;

    displayDeleteButton.value = true;
    isEsTimeslotFormOpened.value = true;
  }
}

const removeTimeslot = (timeslotRequest: TimeslotRequest) => {
  if (timeslotRequest) {
     const indexToRemove = apointmentTypeTimeslotsWithIndex.value
        .findIndex((appointmentTypeTimeslot: ApointmentTypeTimeslotsWithIndex) => appointmentTypeTimeslot.index === timeslotRequest.timeslotId);

      apointmentTypeTimeslotsWithIndex.value.splice(indexToRemove, 1);  
  }

  isEsTimeslotFormOpened.value = false;
  refreshData.value = true;
}

const closeEsTimeslotFormModel = (timeslotRequest: TimeslotRequest) => {
  isEsTimeslotFormOpened.value = false;
  updateTimeslot(timeslotRequest);
}

const updateTimeslot = (timeslotRequest: TimeslotRequest) => {
  if (timeslotRequest) {
    const indexToUpdate = apointmentTypeTimeslotsWithIndex.value
        .findIndex((appointmentTypeTimeslot: ApointmentTypeTimeslotsWithIndex) => appointmentTypeTimeslot.index === timeslotRequest.timeslotId);

    if (indexToUpdate > -1) {
      apointmentTypeTimeslotsWithIndex.value[indexToUpdate].apointmentTypeTimeslots = {
        appointmentTypeId: selectedAppointmentType.value.id,
        start: format(new Date(), DateFormat.ISO_SMALL) + ' ' + formatTime(timeslotRequest.start),
        end: format(new Date(), DateFormat.ISO_SMALL) + ' ' + formatTime(timeslotRequest.end)
      }
    } else {
      const appointmentTypeTimeslots: ApointmentTypeTimeslotsWithIndex = {
        apointmentTypeTimeslots: {
          appointmentTypeId: selectedAppointmentType.value.id,
          start: format(new Date(), DateFormat.ISO_SMALL) + ' ' + formatTime(timeslotRequest.start),
          end: format(new Date(), DateFormat.ISO_SMALL) + ' ' + formatTime(timeslotRequest.end)
        },
        index: timeslotRequest.timeslotId
      };
      apointmentTypeTimeslotsWithIndex.value = apointmentTypeTimeslotsWithIndex.value.concat(appointmentTypeTimeslots);
    }

    const dateToSplit = formatTime(timeslotRequest.start);
    const [hours, minutes] = dateToSplit.split(':').map(Number);

    durationInput.value = {
      hours: hours ,
      minutes: minutes
    };

    refreshData.value = true;
  }
}

const transformToEventInput = (dayModelContent: DayModelContentResponse, idTemp: number, appointmentTypeId: string): EventInput => {
  return {
    id: appointmentTypeId + '_' + idTemp,
    start: format(new Date(), DateFormat.ISO_SMALL) + ' ' + formatTime(dayModelContent.startTime),
    end:   format(new Date(), DateFormat.ISO_SMALL) + ' ' + formatTime(dayModelContent.endTime),
    title: formatTime(dayModelContent.startTime) + ' - ' + formatTime(dayModelContent.endTime),
    color: 'rgba(40, 40, 186, 0.72)'
  }
}

const transformToAppointmentTypeTimeslotsWithId = (eventInput: EventInput, appointmentTypeId: string): ApointmentTypeTimeslotsWithIndex => {
  return {
    index: eventInput.id,
    apointmentTypeTimeslots: {
      appointmentTypeId: appointmentTypeId,
      start: eventInput.start?.toString(),
      end: eventInput.end?.toString()
    }
  }
}

const transformAppointmentTypeTimeslotsToEventInput = (appointmentTypeTimeslotsWithIndex: ApointmentTypeTimeslotsWithIndex): EventInput => {
  return {
    id: appointmentTypeTimeslotsWithIndex.index,
    start: appointmentTypeTimeslotsWithIndex.apointmentTypeTimeslots.start,
    end:   appointmentTypeTimeslotsWithIndex.apointmentTypeTimeslots.end,
    title: format(new Date(appointmentTypeTimeslotsWithIndex.apointmentTypeTimeslots.start), DateFormat.TIME_SMALL) + ' - ' + format(new Date(appointmentTypeTimeslotsWithIndex.apointmentTypeTimeslots.end), DateFormat.TIME_SMALL),
    color: 'rgba(40, 40, 186, 0.72)'
  }
}

const getEventByDay = async (itemId: string) => {
  if (apointmentTypeTimeslotsWithIndex.value.length > 0) {
    return apointmentTypeTimeslotsWithIndex.value
             .filter((appointmentTypeTimeslotsWithIndex: ApointmentTypeTimeslotsWithIndex) => appointmentTypeTimeslotsWithIndex.apointmentTypeTimeslots.appointmentTypeId === itemId)
              .map((appointmentTypeTimeslotsWithIndex: ApointmentTypeTimeslotsWithIndex) => transformAppointmentTypeTimeslotsToEventInput(appointmentTypeTimeslotsWithIndex));
  } else {
    if (itemId && dayModelItemSelect.value && currInstituteUuid.value && selectedHealthPro.value?.id) {
    return await settingsApi.getDayModelById({instituteId: currInstituteUuid.value, daymodelId: dayModelItemSelect.value.id, appointmentTypeId: itemId,doctorId: selectedHealthPro.value.id})
        .then((result:DayModelResponse) => {
          const res = result.dayModelContent.map((dayModelContent: DayModelContentResponse, index) => transformToEventInput(dayModelContent, index, result.appointmentTypeId));
          if (res.length > 0) {
            apointmentTypeTimeslotsWithIndex.value = apointmentTypeTimeslotsWithIndex.value.concat(res.map((eventInput: EventInput) => transformToAppointmentTypeTimeslotsWithId(eventInput, result.appointmentTypeId)));
            backupApointmentTypeTimeslotsWithIndex.value = cloneDeep(apointmentTypeTimeslotsWithIndex.value);
          }
          return res;
      });
    }
  }  
}

watch(
  () => props.isOpen,
  async () => {
    if (props.isOpen) {
      await getDayModel();
    }    
  }
)

onMounted(
  async () => {
    if (props.isOpen) {
      await getDayModel();
    } 
  }
)


return (_ctx: any,_cache: any) => {
  const _component_es_modal_header = _resolveComponent("es-modal-header")!
  const _component_es_modal_footer = _resolveComponent("es-modal-footer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_ctx.isOpen)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createVNode(_component_es_modal_header, { header: _ctx.header }, null, 8, ["header"]),
              _createElementVNode("div", _hoisted_4, [
                (dayModelItemSelect.value)
                  ? (_openBlock(), _createBlock(EsCalendarDayModelContent, {
                      key: 0,
                      selectedDayModel: dayModelItemSelect.value,
                      selectedInstituteId: currInstituteUuid.value,
                      refreshData: refreshData.value,
                      selectedHealthPro: selectedHealthPro.value,
                      getEventByDay: getEventByDay,
                      scroll: durationInput.value,
                      onOpenUpdateTimeslotForm: _cache[0] || (_cache[0] = ($event: any) => (openUpdateTimeslotForm($event))),
                      onOpenAddTimeslotForm: _cache[1] || (_cache[1] = ($event: any) => (openAddTimeslotForm($event))),
                      onUpdateEvent: _cache[2] || (_cache[2] = ($event: any) => (updateEvent($event))),
                      onRefreshed: _cache[3] || (_cache[3] = ($event: any) => (refreshData.value = false))
                    }, null, 8, ["selectedDayModel", "selectedInstituteId", "refreshData", "selectedHealthPro", "scroll"]))
                  : _createCommentVNode("", true)
              ]),
              _createVNode(_component_es_modal_footer, {
                onModalCancel: _cache[4] || (_cache[4] = ($event: any) => (emits('modal-close'))),
                confirmModalMethod: confirmModal,
                "confirm-disabled": !dayModel.value || noChange.value
              }, null, 8, ["confirm-disabled"])
            ], 512)
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(EsTimeslotForm, {
      isOpen: isEsTimeslotFormOpened.value,
      header: _unref(t)('settings.label.specialDays.modal.header'),
      start: _unref(formatTime)(selectedTimeSlot.value.start),
      end: _unref(formatTime)(selectedTimeSlot.value.end),
      timeslotId: selectedTimeSlot.value.timeslotId,
      isSameDate: isSameDate.value,
      isSetting: false,
      min: _unref(minTime),
      max: _unref(maxTime),
      displayDeleteButton: displayDeleteButton.value,
      onModalClose: _cache[5] || (_cache[5] = ($event: any) => (closeEsTimeslotFormModel($event))),
      onDeleteAction: _cache[6] || (_cache[6] = ($event: any) => (removeTimeslot($event)))
    }, null, 8, ["isOpen", "header", "start", "end", "timeslotId", "isSameDate", "min", "max", "displayDeleteButton"])
  ], 64))
}
}

})